import { useEffect, useState } from "react";
import { Box, Button, Card } from "@mui/material";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Timelapse as TimelapseIcon } from "components/icons";
import { useDispatch, useSelector } from "react-redux";
import { Stack } from "@mui/system";
import { ConfirmDialog } from "../ConfirmDialog";
import { WorkingHoursDataGrid } from "./WorkingHoursDataGrid";
import AddWorkingHoursDialog from "./AddWorkingHoursDialog";
import { clientActions } from "store/client-slice";
import { loadAllWorkingHoursByClient, deleteWorkingHour, loadAllLocationsFromBackend } from "app/services/client.service";
import {
  checkUserHasAdminRole,
  checkUserHasBdmRole,
  getBdmClients,
  readSessionCurrentClient,
  readSessionCurrentManager,
} from "app/services/app.state.service";
import { toClientListOptions, toManagerClientOptions } from "utils/clientOptions";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";

const AllWorkingHoursView = () => {
  const dispatch = useDispatch();
  const [isLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [managerClients, setManagerClients] = useState([]);
  // Admin only: every client in the system, read from the session the admin login populates.
  // @since NEXT_VERSION
  const [adminClients, setAdminClients] = useState([]);
  // The accounts this BDM is the relationship manager for. @since NEXT_VERSION
  const [bdmClients, setBdmClients] = useState([]);
  const [workingHoursClientId, setWorkingHoursClientId] = useState(null);
  const workingHours = useSelector((state) => state.client.workingHours);
  const locations = useSelector((state) => state.client.locations);

  // Precedence for a dual-role (ADMIN + EMPLOYEE_MANAGER) user is decided explicitly here:
  // admin wins, so the client dropdown lists every client rather than just their own.
  // Mirrors ExportReportsView.
  const isAdmin = checkUserHasAdminRole();
  // HRMS-116 point 6: a BDM may look but not change. @since NEXT_VERSION
  const isBdm = checkUserHasBdmRole();
  const isReadOnly = isBdm;

  // Working hours are global master data, but the grid is loaded one client at a time, so the
  // dropdown source differs by role: a manager's own client rows vs. every client for an admin.
  // An admin has no _manager row, so readSessionCurrentManager() is empty for them — reading it
  // regardless would leave the dropdown blank and the grid permanently unloaded.
  //
  // A BDM has no _manager row either, and the session never writes CURRENT_MANAGER for them
  // (setManagerSessionPromise only runs for the manager role), so they hit exactly that trap: an
  // empty dropdown, no selected client, and a grid that never loads. Their accounts come from the
  // BDM-to-client assignment instead. @since NEXT_VERSION -- HRMS-116 point 6
  useEffect(() => {
    if (isBdm && !isAdmin) {
      let cancelled = false;
      getBdmClients()
        .then((list) => {
          if (cancelled) return;
          const arr = Array.isArray(list) ? list : [];
          setBdmClients(arr);
          if (arr.length > 0) setWorkingHoursClientId(String(arr[0].id));
        })
        .catch(() => {
          if (!cancelled) setBdmClients([]);
        });
      return () => {
        cancelled = true;
      };
    }

    const list = isAdmin ? readSessionCurrentClient() : readSessionCurrentManager();
    const arr = Array.isArray(list) ? list : [];

    if (isAdmin) setAdminClients(arr);
    else setManagerClients(arr);

    if (arr.length > 0) {
      setWorkingHoursClientId(String(isAdmin ? arr[0].id : arr[0].clientId));
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A BDM's accounts carry the same { id, name, code } shape as an admin's client list, so they
  // share toClientListOptions; a manager's rows are _manager records keyed by clientId instead.
  const clientOptions = isAdmin
    ? toClientListOptions(adminClients)
    : isBdm
      ? toClientListOptions(bdmClients)
      : toManagerClientOptions(managerClients);

  const btnClickHandler = () => {
    if (!workingHoursClientId) return;
    loadAllWorkingHoursByClient(workingHoursClientId)
      .then((response) => dispatch(clientActions.replaceWorkingHours(response.data)))
      .catch((error) => {
        dispatch(clientActions.replaceWorkingHours([]));
        handleAxiosError(error);
      });
  };

  useEffect(() => {
    btnClickHandler();

    if (locations.length === 0) {
      loadAllLocationsFromBackend()
        .then((response) => dispatch(clientActions.replaceLocationData(response.data)))
        .catch((error) => {
          dispatch(clientActions.replaceLocationData([]));
          handleAxiosError(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingHoursClientId, locations.length, dispatch]);

  const editEventHandler = (data) => {
    setOpen(true);
    setEditObject(data);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(`Are you sure you want to delete this Working Hour`);

    const editObjectId = data.id;

    setDeleteId(editObjectId);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteWorkingHour(deleteId)
      .then((response) => {
        btnClickHandler();
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 5,
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            All Working Hours
          </MDTypography>
        </MDBox>
        <MDBox mt={3} px={2} py={0.5}>
          <Stack direction="row" spacing={2}>
            <MDBox>
              <ClientFilterSelect
                value={workingHoursClientId}
                onChange={setWorkingHoursClientId}
                options={clientOptions}
                includeAll={false}
                id="working-hours-client"
              />
            </MDBox>
            {!isReadOnly && (
              <MDBox>
                <Button
                  startIcon={<TimelapseIcon />}
                  onClick={() => {
                    setEditObject(null);
                    setOpen(true);
                  }}
                  sx={{ minWidth: 180, m: 0 }}
                  variant="contained"
                  color="success"
                >
                  Add Working Hour
                </Button>
              </MDBox>
            )}
          </Stack>
        </MDBox>
        <MDBox mb={0} sx={{ overflowX: "auto" }}>
          <WorkingHoursDataGrid
            workingHours={workingHours}
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            editEventHandler={editEventHandler}
            confirmDeleteDialogHandler={confirmDeleteDialogHandler}
          />
        </MDBox>
      </Card>
      <AddWorkingHoursDialog
        open={open}
        setOpen={setOpen}
        clientId={workingHoursClientId}
        refreshActivitiesDataHandler={btnClickHandler}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This Working Hour?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </Box>
  );
};

export default AllWorkingHoursView;
